/* Home screen: animated hero, the two modes, how it works, recent results and the NOVA scale. */

import { qs } from "./dom";
import { NOVA_SHORT, gauge, novaColour } from "./grade";
import { loadHistory } from "./history";
import { icon } from "./icons";
import { every, reducedMotion, reveal } from "./motion";
import type { NovaGroup } from "./types";
import { recentItem } from "./views";

const WORDS = ["food?", "snack?", "breakfast cereal?", "instant noodles?", "masala?"];

/* Floating spice specks in the hero: [left %, top %, size px, delay s, colour]. */
const SPECKS: [number, number, number, number, string][] = [
  [8, 18, 7, 0, "#f2b45f"], [22, 72, 5, 1.2, "#e07a4f"], [38, 30, 4, 2.1, "#fff"],
  [55, 82, 6, 0.6, "#f2b45f"], [68, 14, 5, 1.8, "#c9e27a"], [82, 52, 8, 0.3, "#e07a4f"],
  [90, 22, 4, 2.6, "#fff"], [14, 46, 4, 3, "#c9e27a"], [47, 8, 6, 1.4, "#f2b45f"], [74, 88, 4, 2.3, "#fff"],
];

export function homeView(main: HTMLElement): void {
  const recent = loadHistory().slice(0, 3);
  const specks = SPECKS.map(
    ([x, y, s, d, c]) => `<i style="left:${x}%;top:${y}%;--s:${s}px;--d:${d}s;--c:${c}"></i>`,
  ).join("");

  main.innerHTML = `
  <section class="hero home-hero">
    <div class="specks" aria-hidden="true">${specks}</div>
    <div class="hero-copy">
      <h1 class="serif rise" style="--d:.1s">What’s really in your <span class="rotator"><em>${WORDS[0]}</em></span></h1>
      <p class="rise" style="--d:.2s">Scan it. Know what’s inside.</p>
      <div class="hero-cta rise" style="--d:.3s">
        <a class="btn light big shine" href="#/analyse?scan=1">${icon("scan")}<span>Scan barcode</span></a>
        <a class="btn glass big square" href="#/analyse" aria-label="Type a barcode or upload a label">${icon("keyboard")}</a>
        <a class="btn glass big square" href="#/search" aria-label="Search products by name">${icon("search")}</a>
      </div>
    </div>
    <img class="hero-art rise" style="--d:.4s" src="/img/spices.svg" alt="" width="340" height="230">
  </section>

  <div class="modes">
    <a class="mode card" href="#/analyse" data-reveal style="--d:0s">
      <div class="mode-top">
        <span class="mode-ic green">${icon("analyse")}</span>
        <span class="mode-preview mini-gauge">${gauge(1, "sm")}</span>
      </div>
      <strong>Analyse a product</strong>
      <small>Full report for one pack</small>
      <span class="mode-go">Start ${icon("arrow")}</span>
    </a>
    <a class="mode card" href="#/compare" data-reveal style="--d:.08s">
      <div class="mode-top">
        <span class="mode-ic saffron">${icon("compare")}</span>
        <span class="mode-preview mini-bars"><i style="--h:82%;--c:var(--g-a)"></i><i style="--h:40%;--c:var(--g-e)"></i></span>
      </div>
      <strong>Compare products</strong>
      <small>Khetika vs another brand</small>
      <span class="mode-go">Start ${icon("arrow")}</span>
    </a>
  </div>

  <section class="flow card" data-reveal aria-label="How it works">
    <div><b>${icon("scan")}</b><span>Scan</span></div>
    <i aria-hidden="true">${icon("arrow")}</i>
    <div><b>${icon("camera")}</b><span>Or snap label</span></div>
    <i aria-hidden="true">${icon("arrow")}</i>
    <div><b>${icon("shield")}</b><span>Get verdict</span></div>
  </section>

  ${
    recent.length
      ? `<div class="section-row" data-reveal><h2 class="serif">Recent</h2><a class="link" href="#/recent">See all</a></div>
         <div class="recent-list" data-reveal>${recent.map(recentItem).join("")}</div>`
      : ""
  }

  <section class="insight card" data-reveal>
    <h2 class="serif">Lower NOVA is cleaner.</h2>
    <div class="nova-bars">
      ${([1, 2, 3, 4] as NovaGroup[])
        .map(
          (g, i) => `<div style="--tone:${novaColour(g)};--h:${100 - i * 20}%;--d:${i * 0.1}s">
            <span class="bar"></span><b>${g}</b><small>${NOVA_SHORT[g]}</small></div>`,
        )
        .join("")}
    </div>
    <a class="link" href="#/how">Learn more ${icon("arrow")}</a>
  </section>

  <section class="cta-band" data-reveal>
    <img src="/img/fresh.svg" alt="" width="84" height="64">
    <div><strong class="serif">Know your masala.</strong></div>
    <a class="btn light" href="#/analyse?scan=1">${icon("scan")}<span>Scan now</span></a>
  </section>`;

  reveal(main);
  if (!reducedMotion()) animateHero(main);
}

/** Rotate the headline word. */
function animateHero(main: HTMLElement): void {
  const rotator = qs(main, ".rotator");
  let w = 0;
  every(2600, () => {
    w = (w + 1) % WORDS.length;
    rotator.innerHTML = `<em class="swap-in">${WORDS[w]}</em>`;
  });
}

